import { Button, Input } from '@artosera/ui';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { authApi } from '../../api/auth.js';
import { estApiError } from '../../api/errors.js';
import { capturerPortee, exigerActuelle, estPorteeObsolete } from '../../auth/identite.js';
import { retourSur } from '../../auth/retour.js';
import { useApresConnexion } from '../../auth/session.js';
import { AuthCard } from '../AuthCard.js';
import { Field } from '../Field.js';
import { allerApresConnexion } from './connexionFlow.js';
import styles from './ConnexionPage.module.css';

export function ConnexionSso2FAPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const navigate = useNavigate();
  const apresConnexion = useApresConnexion();
  const [code, setCode] = useState('');
  const [codeSecours, setCodeSecours] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [reprendre, setReprendre] = useState(false);
  const [enCours, setEnCours] = useState(false);

  async function soumettre() {
    const avant = capturerPortee();
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await authApi.ssoDeuxFacteurs({
        ...(code ? { code } : {}),
        ...(codeSecours ? { codeSecours } : {}),
      });
      exigerActuelle(avant);
      const oubli = apresConnexion();
      const apres = capturerPortee();
      await oubli;
      exigerActuelle(apres);
      await allerApresConnexion(navigate, retourSur(reponse.retour));
    } catch (error) {
      if (estPorteeObsolete(error)) return;
      if (estApiError(error)) {
        const raison = (error.details as { raison?: string } | null)?.raison;
        const expiree = raison === 'sso_attente_absente' || raison === 'sso_attente_epuisee';
        setReprendre(expiree);
        setErreur(
          expiree
            ? t('auth.sso.erreurs.etat_invalide')
            : raison === 'mfa_code_invalide'
              ? t('auth.sso.codeInvalide')
              : t('auth.sso.erreurs.inconnue'),
        );
      } else setErreur(t('auth.connexion.erreurReseau'));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <AuthCard
      title={t('auth.sso.deuxFacteurs.titre')}
      subtitle={t('auth.sso.deuxFacteurs.sousTitre')}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void soumettre();
        }}
        noValidate
      >
        <Field
          label={t('auth.deuxFacteurs.champCode')}
          htmlFor="sso-2fa-code"
          className={styles.champCode}
        >
          <Input
            id="sso-2fa-code"
            maxLength={6}
            pattern="[0-9]*"
            inputMode="numeric"
            autoComplete="one-time-code"
            mono
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className={styles.pleineLargeur}
            disabled={reprendre}
          />
        </Field>
        <Field label={t('auth.deuxFacteurs.champCodeSecours')} htmlFor="sso-2fa-secours">
          <Input
            id="sso-2fa-secours"
            maxLength={40}
            autoComplete="off"
            mono
            value={codeSecours}
            onChange={(event) => setCodeSecours(event.target.value)}
            className={styles.pleineLargeur}
            disabled={reprendre}
          />
        </Field>
        {erreur && (
          <p role="alert" className={styles.erreur}>
            {erreur}
          </p>
        )}
        <Button type="submit" className={styles.envoi} loading={enCours} disabled={reprendre}>
          {t('auth.deuxFacteurs.bouton')}
        </Button>
      </form>
      <p className={styles.lienOubli}>
        <Link to="/connexion/sso">
          {t(reprendre ? 'auth.sso.reprendre' : 'auth.deuxFacteurs.retour')}
        </Link>
      </p>
    </AuthCard>
  );
}
